import React, { useEffect } from 'react';
import './Charity.scss';
import Header from '../Header';
import Footer from '../Footer/Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import { FaHeart, FaHandHoldingHeart, FaUsers, FaShieldAlt, FaCalendarAlt, FaGift } from 'react-icons/fa';

const formatCurrency = (value) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '$0.00';
  }
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Sample charity donations data - replace with real data when available
const charityDonationsData = [
  {
    date: '2025-08-01',
    organization: 'Donation incoming in August, 2025',
    amount: 5875.20,
    description: 'Combating human trafficking and modern slavery',
    focus: 'Human Trafficking',
    status: 'Pending'
  }
];

const Charity = () => {
  useEffect(() => {
    document.body.style.overflow = 'auto';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Calculate total donated amount (only completed donations)
  const totalDonated = charityDonationsData
    .filter(donation => donation.status.toLowerCase() === 'completed')
    .reduce((sum, donation) => sum + donation.amount, 0);

  return (
    <div className="charity-page">
      <Header />

      <main className="charity-content">
        {/* Hero Section */}
        <section className="charity-hero-section">
          <div className="container">
            <h1>Reddicks Cares</h1>
            <p className="subtitle">Making a meaningful impact beyond the blockchain through charitable giving and community support.</p>
            
            <div className="charity-value-cards">
              <div className="charity-value-card primary">
                <div className="card-icon">
                  <FaHeart />
                </div>
                <div className="card-content">
                  <h4>Next Charity Fund</h4>
                    <div className="charity-amount">
                      {formatCurrency(charityDonationsData[0].amount)}
                    </div>
                  <p className="data-note">Ready for August 2025 donation</p>
                </div>
              </div>

              <div className="charity-value-card secondary">
                <div className="card-icon">
                  <FaGift />
                </div>
                <div className="card-content">
                  <h4>Total Donated</h4>
                  <div className="charity-amount">
                    {formatCurrency(totalDonated)}
                  </div>
                  <p className="data-note">
                    {totalDonated > 0 
                      ? `Given to ${charityDonationsData.filter(d => d.status.toLowerCase() === 'completed').length} organizations`
                      : `${charityDonationsData.filter(d => d.status.toLowerCase() === 'pending').length} donation pending`
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Charity Section */}
        <section className="charity-about-section">
          <div className="container">
            <h2 className="section-title">Our Charitable Mission</h2>
            <div className="charity-grid">
              <div className="charity-card">
                <FaHandHoldingHeart className="charity-icon" />
                <h3>20% Commitment</h3>
                <p>Twenty percent of all treasury funds are dedicated to charitable causes, ensuring our success creates positive impact in the world.</p>
              </div>
              
              <div className="charity-card">
                <FaUsers className="charity-icon" />
                <h3>Community Driven</h3>
                <p>Our DAO community will vote on future charitable recipients, ensuring transparency and collective decision-making.</p>
              </div>
              
              <div className="charity-card">
                <FaShieldAlt className="charity-icon" />
                <h3>Fighting Trafficking</h3>
                <p>Our initial focus is on organizations combating human trafficking and supporting survivors of modern slavery.</p>
              </div>
              
              <div className="charity-card">
                <FaCalendarAlt className="charity-icon" />
                <h3>Regular Giving</h3>
                <p>Every 6 months, we make substantial donations to approved charitable organizations, creating consistent impact.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Donation History Section */}
        <section className="donation-history-section">
          <div className="container">
            <h2 className="section-title">Donation History</h2>
            <div className="donations-table-container">
              <div className="table-header">
                <h3>Our Charitable Impact</h3>
                <p>Track every donation we've made to ensure complete transparency</p>
              </div>
              
              <div className="donations-table">
                <div className="table-row header-row">
                  <div className="table-cell">Date</div>
                  <div className="table-cell">Organization</div>
                  <div className="table-cell">Amount</div>
                  <div className="table-cell">Focus Area</div>
                  <div className="table-cell">Status</div>
                </div>
                
                {charityDonationsData.map((donation, index) => (
                  <div key={index} className="table-row data-row">
                    <div className="table-cell date-cell">
                      {new Date(donation.date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </div>
                    <div className="table-cell org-cell">
                      <div className="org-info">
                        <h4>{donation.organization}</h4>
                        <p>{donation.description}</p>
                      </div>
                    </div>
                    <div className="table-cell amount-cell">
                      {formatCurrency(donation.amount)}
                    </div>
                    <div className="table-cell focus-cell">
                      <span className="focus-tag">{donation.focus}</span>
                    </div>
                    <div className="table-cell status-cell">
                      <span className={`status-badge ${donation.status.toLowerCase()}`}>
                        {donation.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              {charityDonationsData.length === 0 && (
                <div className="empty-state">
                  <FaHeart className="empty-icon" />
                  <p>No donations recorded yet. Our first charitable giving will begin soon!</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="charity-impact-section">
          <div className="container">
            <h2 className="section-title">Making a Difference</h2>
            <div className="impact-content">
              <div className="impact-text">
                <p>
                  At Reddicks, we believe that financial success should translate into positive real-world impact. 
                  Our commitment to charitable giving isn't just a token gesture—it's a core part of our mission.
                </p>
                <p>
                  By holding REDDICKS tokens, you're not just investing in the future of Radix; you're contributing 
                  to meaningful causes that fight against injustice and support those in need. Every transaction, 
                  every trade, and every holder contributes to our growing charitable fund.
                </p>
                <p>
                  Our initial focus on combating human trafficking reflects our commitment to addressing one of 
                  the world's most pressing humanitarian crises. As our community grows and evolves, so too will 
                  our charitable reach and impact.
                </p>
              </div>
              
              <div className="next-donation-card">
                <div className="card-header">
                  <FaCalendarAlt className="header-icon" />
                  <h3>Next Donation Cycle</h3>
                </div>
                <div className="card-body">
                  <p className="cycle-info">
                    Our next charitable donation is scheduled for <strong>August 2025</strong>
                  </p>
                  <div className="current-fund">
                    <span className="fund-label">Current Fund:</span>
                    <span className="fund-amount">{formatCurrency(5875.20)}</span>
                  </div>
                  <p className="fund-note">
                    This amount is ready for our August 2025 charitable donation to combat human trafficking.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Charity; 